import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { adminApi, tokenStore } from "@/lib/api";
import { LogOut, LayoutGrid, Settings, Sparkles, Globe, ChevronDown, Home, ClipboardList, TrendingUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import JanteLogo from "@/components/JanteLogo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SITE_PAGES = [
  { label: "Home (hosting)", to: "/", Icon: Home },
  { label: "Apply — host application", to: "/onboard", Icon: ClipboardList },
  { label: "Investing leads", to: "/investing", Icon: TrendingUp },
  { label: "Application success page", to: "/success", Icon: CheckCircle2 },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const hasToken = !!tokenStore.get();

  useEffect(() => {
    if (!hasToken) {
      setChecking(false);
      return;
    }
    adminApi
      .get("/auth/me")
      .then((r) => setMe(r.data))
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [hasToken]);

  if (!hasToken) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] text-sm text-[#6E6E70]">
        Loading the studio…
      </div>
    );
  }

  const logout = async () => {
    try {
      await adminApi.post("/auth/logout");
    } catch (_e) {
      /* ignore */
    }
    tokenStore.clear();
    toast.success("Signed out.");
    navigate("/admin/login");
  };

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-[#FAF9F6]">
      <header className="border-b border-[#E3DED5] bg-[#FAF9F6] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-brand-link">
            <span className="inline-block w-2 h-2 rounded-none bg-[#B0895C]" />
            <span className="font-serif-display text-xl text-[#2A1F1A]">
              Sasha<span className="text-[#B0895C]">.</span>host
              <span className="text-[#6E6E70] text-sm ml-2">/ studio</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link
              to="/admin"
              data-testid="admin-nav-dashboard"
              className="text-[#2A1F1A] hover:text-[#C03028] flex items-center gap-2"
            >
              <LayoutGrid size={14} /> Dashboard
            </Link>
            <Link
              to="/admin/scout"
              data-testid="admin-nav-scout"
              className="text-[#2A1F1A] hover:text-[#C03028] flex items-center gap-2"
            >
              <Sparkles size={14} /> Scout
            </Link>
            <Link
              to="/admin/settings"
              data-testid="admin-nav-settings"
              className="text-[#2A1F1A] hover:text-[#C03028] flex items-center gap-2"
            >
              <Settings size={14} /> Settings
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="admin-nav-site-pages"
                className="text-[#6E6E70] hover:text-[#2A1F1A] flex items-center gap-1.5 outline-none"
              >
                <Globe size={14} /> Site pages <ChevronDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-[#FAF9F6] border border-[#E3DED5] rounded-none min-w-[240px]"
              >
                {SITE_PAGES.map(({ label, to, Icon }) => (
                  <DropdownMenuItem key={to} asChild className="rounded-none cursor-pointer">
                    <a
                      href={to}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-site-page-${to === "/" ? "home" : to.replace(/\//g, "")}`}
                      className="flex items-center gap-2 text-sm text-[#2A1F1A]"
                    >
                      <Icon size={14} className="text-[#B0895C]" /> {label}
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-4">
            {me?.email && (
              <span
                className="text-xs text-[#6E6E70] hidden sm:inline"
                data-testid="admin-user-email"
              >
                {me.email}
              </span>
            )}
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="btn-secondary inline-flex items-center gap-2 px-3 py-2 text-xs tracking-wide rounded-none"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <Outlet context={{ me }} />
    </div>
  );
}
